import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FadeUpText, ParallaxText, RevealText } from '@/components/AnimatedText';
import { ArrowRight, Loader2 } from 'lucide-react';
import { pagesApi, researchApi, notesApi } from '@/lib/supabase';
import { useLocalizedContent } from '@/hooks/useLocalizedContent';
import { optimizeImageUrl } from '@/lib/imageOptimize';

const Home = () => {
  const containerRef = useRef(null);
  const visualCulturesRef = useRef(null);
  const [pageData, setPageData] = useState(null);
  const [researchData, setResearchData] = useState([]);
  const [notesData, setNotesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { getField, getContent, isArabic } = useLocalizedContent();
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 0.95]);
  const y = useTransform(scrollYProgress, [0, 0.3], [0, 50]);

  const { scrollYProgress: visualCulturesScroll } = useScroll({
    target: visualCulturesRef,
    offset: ['start end', 'end start']
  });
  
  const visualCulturesColor = useTransform(
    visualCulturesScroll,
    [0, 0.5, 1],
    ['#1C1C1C', '#8B6F47', '#1C1C1C']
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [page, projects, notes] = await Promise.all([
          pagesApi.getPublishedBySlug('home').catch(() => null),
          researchApi.getPublished(),
          notesApi.getPublished()
        ]);
        setPageData(page);
        setResearchData(projects || []);
        setNotesData(notes || []);
      } catch (err) {
        console.error('Error fetching home data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get sections by type with localized content
  const getSection = (type) => {
    const section = pageData?.sections?.find(s => s.section_type === type);
    if (!section) return null;
    return {
      ...section,
      content: getContent(section)
    };
  };
  const heroSection = getSection('hero');
  const statementSection = getSection('statement');
  const contactTeaserSection = getSection('contact_teaser');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="min-h-screen" data-testid="home-container">
      {/* Hero Section */}
      <motion.section 
        style={{ opacity, scale, y }}
        className="min-h-screen flex flex-col justify-center px-6 md:px-12 pt-24 pb-24 relative"
      >
        <div className="max-w-5xl">
          <RevealText delay={0.2}>
            <span className="caption text-muted-foreground block mb-6" data-testid="home-subtitle">
              {heroSection?.content?.subtitle || t('home.subtitle')}
            </span>
          </RevealText>
          
          <RevealText delay={0.4}>
            <h1 className="heading-display mb-10 md:mb-8" data-testid="home-main-heading">
              {heroSection?.content?.heading_line1 || 'At the intersection of archives,'}
              <br />
              <motion.em 
                ref={visualCulturesRef}
                style={{ color: visualCulturesColor }}
                className="group cursor-default"
                whileHover={{ color: '#8B6F47' }}
              >
                {heroSection?.content?.heading_line2 || 'visual cultures'}
              </motion.em>,
              <br />
              {heroSection?.content?.heading_line3 || 'and resistance.'}
            </h1>
          </RevealText>
          
          <FadeUpText delay={0.6} className="max-w-xl mb-20 md:mb-16">
            <p className="body-large text-muted-foreground leading-relaxed" data-testid="home-intro-text">
              {heroSection?.content?.description || 'A research-based curatorial practice exploring postcolonial studies, decolonial feminist methodologies, and the visual politics of the Middle East.'}
            </p>
          </FadeUpText>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            <span className="caption text-muted-foreground">{t('common.scrollToExplore')}</span>
          </motion.div>
        </div>
      </motion.section>

      {/* Statement Section */}
      {statementSection && (
        <section className="section-container">
          <div className="grid md:grid-cols-12 gap-6 md:gap-16">
            <div className="md:col-span-4">
              <FadeUpText>
                <span className="caption text-muted-foreground">{statementSection.content?.label || 'Statement'}</span>
              </FadeUpText>
            </div>
            <div className="md:col-span-8">
              {statementSection.content?.main_text && (
                <FadeUpText delay={0.1}>
                  <p className="heading-medium mb-10 md:mb-8 leading-snug">
                    {statementSection.content.main_text}
                  </p>
                </FadeUpText>
              )}
              {statementSection.content?.body_text && (
                <FadeUpText delay={0.2}>
                  <p className="body-large text-muted-foreground mb-10 md:mb-8 leading-relaxed">
                    {statementSection.content.body_text}
                  </p>
                </FadeUpText>
              )}
              {statementSection.content?.quote && (
                <FadeUpText delay={0.3}>
                  <p className="fragment">
                    {statementSection.content.quote}
                  </p>
                </FadeUpText>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Featured Research */}
      <section className="py-24 md:py-32">
        <div className="px-6 md:px-12 mb-12">
          <FadeUpText>
            <div className="flex items-center justify-between">
              <span className="caption text-muted-foreground">{t('home.selectedResearch')}</span>
              <Link to="/research" className="caption link-subtle flex items-center gap-2">
                {t('common.viewAll')} <ArrowRight size={14} className={isArabic ? 'rotate-180' : ''} />
              </Link>
            </div>
          </FadeUpText>
        </div>
        
        {researchData.length > 0 ? (
          <div className="horizontal-scroll px-6 md:px-12">
            {researchData.map((project, index) => (
              <FeaturedProject 
                key={project.id}
                title={getField(project, 'title')}
                description={getField(project, 'excerpt')}
                year={project.year}
                index={String(index + 1).padStart(2, '0')}
                image={project.phases?.[0]?.image || ''}
                slug={project.slug}
              />
            ))}
          </div>
        ) : (
          <div className="px-6 md:px-12">
            <p className="body-small text-muted-foreground">{t('research.noProjects')}</p>
          </div>
        )}
      </section>

      {/* Reading Room Preview */}
      <section className="section-container border-t border-border">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-4">
            <FadeUpText>
              <span className="caption text-muted-foreground">{t('home.fromReadingRoom')}</span>
            </FadeUpText>
          </div>
          <div className="md:col-span-8">
            {notesData.length > 0 ? (
              <>
                {notesData.slice(0, 2).map((note, index) => (
                  <FadeUpText key={note.id} delay={0.1 * (index + 1)}>
                    <Link 
                      to={`/notes/${note.slug}`} 
                      className={`block group ${index === 0 ? 'mb-8 pb-8 border-b border-border' : 'mb-8'}`}
                    >
                      <span className="caption text-[#1C1C1C] mb-2 block">{note.date}</span>
                      <h3 className="heading-medium mb-3 group-hover:text-[#8B6F47] transition-colors">
                        {getField(note, 'title')}
                      </h3>
                      <p className="body-small text-[#1C1C1C]">
                        {getField(note, 'excerpt')}
                      </p>
                    </Link>
                  </FadeUpText>
                ))}
              </>
            ) : (
              <p className="body-small text-muted-foreground mb-8">{t('notes.noNotes')}</p>
            )}
            <FadeUpText delay={0.3}>
              <Link to="/notes" className="caption link-subtle flex items-center gap-2">
                {t('home.enterReadingRoom')} <ArrowRight size={14} className={isArabic ? 'rotate-180' : ''} />
              </Link>
            </FadeUpText>
          </div>
        </div>
      </section>

      {/* Contact Teaser */}
      {contactTeaserSection && (
        <section className="section-container">
          <div className="text-center py-16">
            <FadeUpText>
              <p className="fragment mb-6">{contactTeaserSection.content?.text || 'Open to conversations, collaborations, and provocations.'}</p>
            </FadeUpText>
            <FadeUpText delay={0.1}>
              <Link 
                to={contactTeaserSection.content?.link_url || '/cv'} 
                className="heading-large text-[#1C1C1C] hover:text-[#8B6F47] transition-colors duration-300"
              >
                {contactTeaserSection.content?.link_text || t('home.getInTouch') + ' →'}
              </Link>
            </FadeUpText>
          </div>
        </section>
      )}
    </div>
  );
};

const FeaturedProject = ({ title, description, year, index, image, slug }) => {
  return (
    <Link 
      to={`/research/${slug}`}
      className="group flex-shrink-0 w-[300px] md:w-[400px]"
      data-testid={`featured-project-${index}`}
    >
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="bg-[#F5F2EB] border border-[#E5E0D6] h-full overflow-hidden hover:border-[#1C1C1C] transition-colors"
      >
        <div className="aspect-[4/3] overflow-hidden">
          {image ? (
            <img 
              src={optimizeImageUrl(image, 600)} 
              alt={title}
              loading="lazy"
              width="400"
              height="300"
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:sepia transition-all duration-700"
            />
          ) : (
            <div className="w-full h-full bg-[#E5E0D6]" />
          )}
        </div>
        <div className="p-6 md:p-8">
          <span className="caption text-muted-foreground block mb-4">{index}</span>
          <h3 className="heading-medium mb-3 group-hover:text-primary transition-colors">
            {title}
          </h3>
          <p className="body-small text-muted-foreground mb-6">
            {description}
          </p>
          <span className="caption text-muted-foreground">{year}</span>
        </div>
      </motion.div>
    </Link>
  );
};

export default Home;
